import { events, type EventItem } from "@/mocks/events";
import { specials, type SpecialTopic } from "@/mocks/specials";

export function getAllEvents(): EventItem[] {
  return events;
}

export function getEventById(id: string): EventItem | undefined {
  return events.find((item) => item.id === id);
}

export function getFeaturedEvents(limit = 3): EventItem[] {
  return [...events].sort((a, b) => b.heat - a.heat).slice(0, limit);
}

export function getLatestEvents(limit = 6): EventItem[] {
  return [...events]
    .sort((a, b) => (a.startDate < b.startDate ? 1 : -1))
    .slice(0, limit);
}

export function getRelatedEvents(current: EventItem, limit = 3): EventItem[] {
  const sameCategory = events.filter(
    (item) => item.id !== current.id && item.category === current.category
  );
  const others = events.filter(
    (item) => item.id !== current.id && item.category !== current.category
  );
  return [...sameCategory, ...others].slice(0, limit);
}

export function getSpecialById(id: string): SpecialTopic | undefined {
  return specials.find((topic) => topic.id === id);
}

export function getSpecialEvents(topic: SpecialTopic): EventItem[] {
  return topic.eventIds
    .map((id) => getEventById(id))
    .filter((item): item is EventItem => Boolean(item));
}

export function searchEvents(keyword: string, limit = 6): EventItem[] {
  const query = keyword.trim().toLowerCase();
  if (!query) return [];
  return events
    .filter((item) => {
      const haystack = [item.title, item.category, item.region, item.format, ...item.tags]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    })
    .slice(0, limit);
}

export interface EventFilterOptions {
  category: string;
  region: string;
  dateRange: string;
  tags: string[];
  status: string;
  keyword: string;
}

const dateRangeDays: Record<string, number> = {
  "全部时间": 99999,
  "近 3 天": 3,
  "近 7 天": 7,
  "近 15 天": 15,
  "近 30 天": 30,
};

export function filterEvents(options: EventFilterOptions): EventItem[] {
  const { category, region, dateRange, tags, status, keyword } = options;
  const query = keyword.trim().toLowerCase();
  const now = new Date("2026-10-12").getTime();
  const maxDays = dateRangeDays[dateRange] ?? 99999;

  return events.filter((item) => {
    if (category && category !== "全部类型" && item.category !== category) return false;
    if (region && region !== "全部地区" && item.region !== region) return false;
    if (status && status !== "全部状态" && item.status !== status) return false;
    if (tags.length > 0 && !item.tags.some((tag) => tags.includes(tag))) return false;
    if (dateRange && dateRange !== "全部时间") {
      const diff = Math.abs(new Date(item.startDate).getTime() - now) / 86400000;
      if (diff > maxDays) return false;
    }
    if (query) {
      const haystack = [item.title, item.category, item.region, item.format, ...item.tags]
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(query)) return false;
    }
    return true;
  });
}

export function sortEvents(list: EventItem[], sortBy: string): EventItem[] {
  const sorted = [...list];
  if (sortBy === "heat") {
    sorted.sort((a, b) => b.heat - a.heat);
  } else if (sortBy === "name") {
    sorted.sort((a, b) => a.title.localeCompare(b.title, "zh-Hans-CN"));
  } else {
    sorted.sort((a, b) => (a.startDate < b.startDate ? 1 : -1));
  }
  return sorted;
}